import { useCallback, useEffect, useState } from 'react';

import { useTheme } from '@rover/kibble/themes';
import { AnnouncementType } from '@rover/types';

type GetAnnouncementStyle = {
  background?: string;
  color?: string;
  border?: string;
  fill?: string;
};

const useGetAnnouncementStyle = (
  announcementType?: AnnouncementType,
  backgroundImageSrc?: string | null
): GetAnnouncementStyle => {
  const theme = useTheme();
  const [background, setBackground] = useState<string | undefined>(undefined);
  const [color, setColor] = useState<string | undefined>(undefined);
  const [border, setBorder] = useState<string | undefined>(undefined);
  const [fill, setFill] = useState<string | undefined>(undefined);

  const setBackgroundProperty = useCallback(
    (backgroundColor?: string) => {
      if (backgroundImageSrc) {
        setBackground(`url(${backgroundImageSrc}) top center no-repeat`);
      } else {
        setBackground(backgroundColor);
      }
    },
    [backgroundImageSrc]
  );

  useEffect(() => {
    switch (announcementType) {
      case AnnouncementType.DANGER:
        setBackgroundProperty(theme.colors.red[600].toString());
        setColor(theme.textColor.primary_inverse);
        setBorder(undefined);
        setFill(theme.colors.neutral.white.toString());
        break;
      case AnnouncementType.COVID19:
      case AnnouncementType.UPDATE_BIRTHDATE_REQUIRED:
        setBackgroundProperty(theme.colors.cyan[200].toString());
        setColor(theme.textColor.primary);
        setBorder(undefined);
        setFill(theme.colors.neutral[700].toString());
        break;
      case AnnouncementType.IDENTITY_VERIFICATION_PASSED:
        setBackgroundProperty(theme.colors.green[100].toString());
        setColor(theme.textColor.primary);
        setBorder(`2px solid ${theme.colors.green[600].toString()}`);
        setFill(theme.colors.neutral[700].toString());
        break;
      case AnnouncementType.SOFT_LAUNCH_ANNOUNCEMENT:
        setBackgroundProperty(theme.colors.orange[300].toString());
        setColor(theme.textColor.primary);
        setBorder(undefined);
        setFill(theme.colors.neutral.white.toString());
        break;
      case AnnouncementType.INFO:
      default:
        setBackgroundProperty(theme.background.caution);
        setColor(theme.textColor.primary);
        setBorder(undefined);
        setFill(theme.colors.neutral[700].toString());
        break;
    }
  }, [theme, announcementType, setBackgroundProperty]);

  return { background, color, border, fill };
};

export default useGetAnnouncementStyle;
